import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Plus } from 'lucide-react';
import { intercompanyAPI, entitiesAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

/**
 * Record an intercompany settlement (Fix 2b).
 *
 * Two-step gate for a write to live accounting: Preview shows the before/after
 * pair position and which loans would be netted, then Apply records the
 * settlement and flips the matching loans so the position zeroes.  Apply rides
 * the app's own authenticated API client and carries an idempotency key so a
 * repeated Apply cannot double-post.
 */
function makeIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Deterministic-enough fallback for older browsers.
  return 'k-' + Date.now() + '-' + Math.round(Math.random() * 1e9);
}

export function IntercompanySettlementControl({ onApplied }) {
  var [open, setOpen] = useState(false);
  var [entities, setEntities] = useState([]);
  var [debtor, setDebtor] = useState('');
  var [creditor, setCreditor] = useState('');
  var [amount, setAmount] = useState('');
  var [currency, setCurrency] = useState('EUR');
  var [reference, setReference] = useState('');
  var [date, setDate] = useState('');
  var [preview, setPreview] = useState(null);
  var [idempotencyKey, setIdempotencyKey] = useState(null);
  var [loading, setLoading] = useState(false);
  var [applying, setApplying] = useState(false);

  // Load the entity list only when the form is opened.  Keeps the collapsed
  // positions view free of an extra request (and dependency).
  useEffect(function () {
    if (!open || entities.length > 0) return;
    entitiesAPI.getAll().then(function (res) {
      var list = res.data || [];
      list.sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); });
      setEntities(list);
    }).catch(function () {});
  }, [open]);

  var body = function () {
    return {
      debtor_entity: debtor,
      creditor_entity: creditor,
      amount: parseFloat(amount),
      currency: currency,
      reference: reference,
      date: date,
    };
  };

  var valid = debtor && creditor && debtor !== creditor && parseFloat(amount) > 0;

  // Echo which relationship is being settled: the netted Positions row is one
  // per counterparty, so the settlement must name the specific (debtor, creditor,
  // currency) relationship it nets, not just the counterparty.
  var nameOf = function (id) {
    for (var i = 0; i < entities.length; i++) { if (entities[i].id === id) return entities[i].name; }
    return id;
  };

  var runPreview = async function () {
    if (!valid) return;
    setLoading(true);
    setPreview(null);
    try {
      var res = await intercompanyAPI.settlementPreview(body());
      setPreview(res.data);
      // Mint one idempotency key per previewed settlement and reuse it for
      // Apply, so a double-click on Apply cannot double-post.  A changed field
      // clears the preview (and thus this key), and the next preview mints a new
      // one for the genuinely different settlement.
      setIdempotencyKey(makeIdempotencyKey());
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var runApply = async function () {
    if (!preview || !idempotencyKey) return;
    if (!window.confirm('Record this settlement to live accounting?\n\n'
      + debtor + ' → ' + creditor + ' ' + amount + ' ' + currency
      + '\nAfter: ' + preview.after_native + ' ' + currency)) return;
    setApplying(true);
    try {
      var payload = Object.assign({}, body(), { idempotency_key: idempotencyKey });
      var res = await intercompanyAPI.settlementApply(payload);
      toast.success('Settlement ' + (res.data.status || 'applied') + ' (' + (res.data.loans_settled || 0) + ' loans netted)');
      setPreview(null);
      setIdempotencyKey(null);
      setAmount('');
      setOpen(false);
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={function () { setOpen(true); }} data-testid="record-settlement-open">
        <Plus className="h-4 w-4 mr-1.5" /> Record settlement
      </Button>
    );
  }

  return (
    <div className="border rounded-lg p-3 space-y-3 text-sm" data-testid="settlement-control">
      <div className="font-medium">Record intercompany settlement</div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-muted-foreground">
          Debtor (paid)
          <select className="mt-1 w-full text-sm border rounded px-2 py-1.5 bg-background" value={debtor}
            onChange={function (e) { setDebtor(e.target.value); setPreview(null); }}>
            <option value="">Select...</option>
            {entities.map(function (ent) { return <option key={ent.id} value={ent.id}>{ent.name}</option>; })}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          Creditor (was owed)
          <select className="mt-1 w-full text-sm border rounded px-2 py-1.5 bg-background" value={creditor}
            onChange={function (e) { setCreditor(e.target.value); setPreview(null); }}>
            <option value="">Select...</option>
            {entities.map(function (ent) { return <option key={ent.id} value={ent.id}>{ent.name}</option>; })}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          Amount
          <input className="mt-1 w-full text-sm border rounded px-2 py-1.5 bg-background" value={amount}
            onChange={function (e) { setAmount(e.target.value); setPreview(null); }} placeholder="40000" />
        </label>
        <label className="text-xs text-muted-foreground">
          Currency
          <select className="mt-1 w-full text-sm border rounded px-2 py-1.5 bg-background" value={currency}
            onChange={function (e) { setCurrency(e.target.value); setPreview(null); }}>
            <option value="GBP">GBP</option>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          Date (optional)
          <input className="mt-1 w-full text-sm border rounded px-2 py-1.5 bg-background" value={date}
            onChange={function (e) { setDate(e.target.value); }} placeholder="2026-07-02" />
        </label>
        <label className="text-xs text-muted-foreground">
          Reference (optional)
          <input className="mt-1 w-full text-sm border rounded px-2 py-1.5 bg-background" value={reference}
            onChange={function (e) { setReference(e.target.value); }} placeholder="SCI 113 repayment" />
        </label>
      </div>

      {preview && (
        <div className="bg-muted/50 rounded p-3 font-mono text-xs space-y-1" data-testid="settlement-preview">
          <div className="flex justify-between text-muted-foreground" data-testid="settlement-relationship">
            <span>relationship</span><span>{nameOf(debtor) + ' → ' + nameOf(creditor) + ' · ' + currency}</span>
          </div>
          <div className="flex justify-between"><span>before</span><span>{preview.before_native} {preview.currency}</span></div>
          <div className="flex justify-between"><span>after</span><span>{preview.after_native} {preview.currency}</span></div>
          <div className="flex justify-between"><span>loans netted</span><span>{(preview.loans_to_settle || []).length}</span></div>
          {preview.fully_settles
            ? <Badge variant="default" className="bg-green-600 text-[10px]">fully settles</Badge>
            : <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-400">partial</Badge>}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={runPreview} disabled={!valid || loading} data-testid="settlement-preview-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Preview'}
        </Button>
        <Button size="sm" onClick={runApply} disabled={!preview || applying} data-testid="settlement-apply-btn">
          {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
        </Button>
        <Button variant="ghost" size="sm" onClick={function () { setOpen(false); setPreview(null); }}>Cancel</Button>
      </div>
    </div>
  );
}
